import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Button,
  Alert,
  LinearProgress,
  Paper,
} from '@mui/material';
import {
  ArrowUpward,
  ArrowDownward,
  Delete,
  PictureAsPdf,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { categoryService } from '../services/api';

// Per-PDF row. Name commits on blur to avoid spamming onChange on every keystroke.
const PdfRow = ({ pdf, isFirst, isLast, onRename, onMove, onDelete, t }) => {
  const [name, setName] = useState(pdf.name);
  const [busy, setBusy] = useState(false);

  const commitRename = () => {
    const trimmed = name.trim();
    if (!trimmed || trimmed === pdf.name) {
      setName(pdf.name);
      return;
    }
    onRename(trimmed);
  };

  const handleDelete = async () => {
    if (!window.confirm(t('categories.confirmDeletePdf'))) return;
    setBusy(true);
    try {
      await onDelete();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
      <PictureAsPdf color="error" />
      <TextField
        value={name}
        onChange={(e) => setName(e.target.value)}
        onBlur={commitRename}
        size="small"
        variant="standard"
        sx={{ flex: 1 }}
        disabled={busy}
      />
      <IconButton size="small" onClick={() => onMove(-1)} disabled={isFirst || busy} title={t('common.moveUp') || 'Up'}>
        <ArrowUpward fontSize="small" />
      </IconButton>
      <IconButton size="small" onClick={() => onMove(1)} disabled={isLast || busy} title={t('common.moveDown') || 'Down'}>
        <ArrowDownward fontSize="small" />
      </IconButton>
      <IconButton size="small" color="error" onClick={handleDelete} disabled={busy} title={t('common.delete')}>
        <Delete fontSize="small" />
      </IconButton>
    </Box>
  );
};

// Manages the PDFs attached to a "pdf" category. Mirrors VideoPdfsManager but
// talks to categoryService. Requires an already-saved categoryId.
const CategoryPdfsManager = ({ categoryId, pdfs, onChange }) => {
  const { t } = useTranslation();
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  if (!categoryId) {
    return (
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('categories.pdfs')}</Typography>
        <Alert severity="info">{t('categories.pdfsSaveFirst')}</Alert>
      </Box>
    );
  }

  const sorted = [...(pdfs || [])].sort((a, b) => (a.order || 0) - (b.order || 0));

  // Apply a new ordering (entries already have updated order assigned by the caller).
  const applyChange = (next) => onChange(next.map((p, i) => ({ ...p, order: i })));

  const handleAdd = async (file) => {
    if (!file) return;
    if (file.type !== 'application/pdf') {
      setUploadError('Only PDF files are allowed');
      return;
    }
    setUploadError('');
    setUploading(true);
    try {
      const { pdf } = await categoryService.uploadPdf(categoryId, file, file.name.replace(/\.pdf$/i, ''));
      applyChange([...sorted, pdf]);
    } catch (err) {
      setUploadError(err.response?.data?.message || err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleRename = (pdfId, newName) => {
    applyChange(sorted.map((p) => (p._id === pdfId ? { ...p, name: newName } : p)));
  };

  const handleMove = (pdfId, delta) => {
    const idx = sorted.findIndex((p) => p._id === pdfId);
    const targetIdx = idx + delta;
    if (idx < 0 || targetIdx < 0 || targetIdx >= sorted.length) return;
    const next = [...sorted];
    [next[idx], next[targetIdx]] = [next[targetIdx], next[idx]];
    applyChange(next);
  };

  const handleDelete = async (pdfId) => {
    try {
      await categoryService.deletePdf(categoryId, pdfId);
      applyChange(sorted.filter((p) => p._id !== pdfId));
    } catch (err) {
      setUploadError(err.response?.data?.message || err.message || 'Delete failed');
    }
  };

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('categories.pdfs')}</Typography>
      {sorted.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {t('categories.noPdfs')}
        </Typography>
      )}
      {sorted.length > 0 && (
        <Paper variant="outlined" sx={{ mb: 2 }}>
          {sorted.map((pdf, i) => (
            <PdfRow
              key={pdf._id}
              pdf={pdf}
              isFirst={i === 0}
              isLast={i === sorted.length - 1}
              onRename={(newName) => handleRename(pdf._id, newName)}
              onMove={(delta) => handleMove(pdf._id, delta)}
              onDelete={() => handleDelete(pdf._id)}
              t={t}
            />
          ))}
        </Paper>
      )}
      <Box>
        <Button
          variant="outlined"
          component="label"
          startIcon={<PictureAsPdf />}
          disabled={uploading}
        >
          {t('categories.addPdf')}
          <input
            type="file"
            accept=".pdf,application/pdf"
            hidden
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = '';
              handleAdd(file);
            }}
          />
        </Button>
        {uploading && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary">{t('categories.pdfUploading')}</Typography>
            <LinearProgress sx={{ mt: 0.5 }} />
          </Box>
        )}
      </Box>
      {uploadError && (
        <Alert severity="error" sx={{ mt: 1 }} onClose={() => setUploadError('')}>
          {uploadError}
        </Alert>
      )}
    </Box>
  );
};

export default CategoryPdfsManager;
